import React, { type PropsWithChildren } from "react";

import cx from "classnames";
import { constructCurrency } from "common/currency";
import { Odds, ShareIcon } from "@picklebet/ui";
import has from "lodash/has";
import { BetSummaryContext, useBetSummaryContext } from "./BetSummaryContext";
import { ShareEntryAction } from "../share/ShareEntryAction";
import { useShareEntryModal } from "../../hooks/useShareEntryModal";
import { Payout } from "@/features/betslip/components/parts/Payout";
import { usePreferences } from "@/hooks/firestore/user/usePreferences";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { combinations as calculateCombinations } from "mathjs";
import { ShinyText } from "@picklebet/ui";
import type { ProcessedBetEntry } from "@/hooks/firestore/betting/useEntries";
import type { OddsFormat } from "types/BetTypes";

import {
  getEntryStatus,
  getOdds,
  isEntryCashedOut,
  isEntryDecided,
  isEntrySP,
} from "features/entries/util/entry-helpers";
import {
  isExoticSelection,
  isQuaddieSelection,
} from "features/entries/util/type-guards";

import * as styles from "./BetSummary.module.scss";

type BetSummaryProps = PropsWithChildren<{
  entry: ProcessedBetEntry;
  isCompact: boolean;
  isWon: boolean;
  isCashedOut?: boolean;
}>;

type BetStakeProps = {
  separator?: React.ReactNode;
  omitStakeValue?: boolean;
  label?: string;
};

type BetOddsSummaryProps = {
  separator?: React.ReactNode;
};

type BetStakePerComboProps = {
  separator?: React.ReactNode;
  omitStakeValue?: boolean;
};

type BetStakeHiddenProps = {
  label?: string;
};

export const BetSummary = ({
  entry,
  isCompact,
  isWon,
  isCashedOut,
  children,
}: BetSummaryProps) => {
  const currency = useFiatCurrency();
  const oddsFormat = usePreferences().preferences?.oddsFormat?.toLowerCase();

  return (
    <BetSummaryContext.Provider
      value={{ entry, currency, oddsFormat: oddsFormat ?? "" }}
    >
      <div
        className={cx(styles.summary, {
          [styles.isWon]: isWon,
          [styles.isCompact]: isCompact,
          [styles.isCashedOut]: isCashedOut,
        })}
      >
        {children}
      </div>
    </BetSummaryContext.Provider>
  );
};

export const BetStake = ({
  separator,
  omitStakeValue,
  label,
}: BetStakeProps) => {
  const { entry, currency } = useBetSummaryContext();

  const isPromo = entry.data.stakeSource === "PROMOTION_USER";
  const stake = entry.data.stake;

  if (omitStakeValue) return <BetStakeHidden label={label ?? "Stake"} />;

  return (
    <div className={cx({ [styles.isPromo]: isPromo })}>
      <span className={styles.label}>{label ?? "Stake"}</span>
      <span className={styles.value}>
        <div>{constructCurrency(stake, { currencyType: currency })}</div>

        {isPromo && <div className={styles.bonus}>bonus</div>}
      </span>
      <span className={cx({ [styles.circle]: !!separator })}>{separator}</span>
    </div>
  );
};

export const BetStakeHidden = ({ label }: BetStakeHiddenProps) => {
  return (
    <div>
      <div className={styles.stakeHidden}>
        <div>
          <div className={styles.label}>{label ?? "Stake"}</div>
          <div className={styles.value}>HIDDEN</div>
        </div>
        <div className={styles.emoji}>🤫</div>
      </div>
    </div>
  );
};

export const BetOddsSummary = ({ separator }: BetOddsSummaryProps) => {
  const { entry, oddsFormat } = useBetSummaryContext();

  const [message] = getEntryStatus(entry);

  const isMulti = entry.data.selections.length > 1;
  const spOdds = isMulti ? "-" : "SP";
  const isCashedOut = isEntryCashedOut(entry);
  const sp = isEntrySP(entry);

  return (
    <div className={cx(styles.odds, { [styles.oddsOnly]: !separator })}>
      <span className={styles.label}>Odds</span>
      <span className={styles.value}>
        {sp ? (
          spOdds
        ) : (
          <Odds
            base={getOdds(entry)}
            format={oddsFormat as OddsFormat}
            zero={`1.00`}
            isVoid={message === "VOID" || isCashedOut}
            removeVoidLabel={isCashedOut}
            className={cx({
              [styles.cashedOutOdds]: isCashedOut,
            })}
          />
        )}
      </span>
      <span className={cx({ [styles.circle]: !!separator })}>{separator}</span>
    </div>
  );
};

export const BetStakePerCombo = ({
  separator,
  omitStakeValue,
}: BetStakePerComboProps) => {
  const { entry } = useBetSummaryContext();
  const currency = useFiatCurrency();

  const isQuaddie = isQuaddieSelection(entry.data.selections[0]);
  const isExotic = isExoticSelection(entry.data.selections[0]);
  const isCombo = !!entry.data.selectionsRequiredToWin;

  const stake = entry.data.stake;
  const totalCombinations = entry.data.totalCombinations;

  const numberOfExoticCombinations =
    entry.data.selections.find((selection) =>
      has(selection, "exoticCombinations"),
    )?.exoticCombinations ?? 0;

  const combinationsCount = isCombo
    ? calculateCombinations(
        entry.data.selections.length,
        entry.data.selectionsRequiredToWin ?? 0,
      )
    : 0;

  const stakePerComboInCents =
    isQuaddie && totalCombinations
      ? stake / totalCombinations
      : isExotic
        ? stake / numberOfExoticCombinations
        : stake / combinationsCount;

  const label = "Stake/Combo";

  if (omitStakeValue) return <BetStakeHidden label={label} />;

  return (
    <div className={cx(styles.odds, { [styles.oddsOnly]: !separator })}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        <div>
          {stakePerComboInCents > 0 && stakePerComboInCents < 1
            ? `<$0.01`
            : constructCurrency(stakePerComboInCents, {
                currencyType: currency,
              })}
        </div>
      </span>
      <span className={cx({ [styles.circle]: !!separator })}>{separator}</span>
    </div>
  );
};

export const BetPayout = () => {
  const { entry } = useBetSummaryContext();

  const sp = isEntrySP(entry);
  const isCombo = !!entry.data.selectionsRequiredToWin;
  const isExotic = isExoticSelection(entry.data.selections[0]);
  const decided = isEntryDecided(entry);
  const payOut = decided ? entry.data.paidOut : entry.data.potentialPayout;

  let payoutAmount: number | string = payOut;

  if (isExotic) {
    payoutAmount = decided ? payOut : "TBD";
  } else if (isCombo) {
    const totalSelections = entry.data.selections.length;

    const losses = entry.data.selections.reduce(
      (acc, selection) => (selection.result === "LOSE" ? ++acc : acc),
      0,
    );

    // Check if it's impossible to form a winning combination
    if (totalSelections - losses < (entry.data.selectionsRequiredToWin ?? 0)) {
      payoutAmount = 0;
    } else if (!decided && losses > 0) {
      // when there is at least one losing selection but a winning combination can still be formed
      payoutAmount = "TBD";
    } else {
      // get it from the backend
      payoutAmount = payOut;
    }
  }

  // Payout Amount
  if (payoutAmount !== "TBD" && !decided && sp) {
    payoutAmount = "TBD";
  }

  const label = `${isCombo && !decided ? "Est. " : ""}Payout`;

  return (
    <div>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {decided && typeof payoutAmount === "number" && payoutAmount > 0 ? (
          <ShinyText duration={1.2} repeatDelay={3}>
            <Payout amount={payoutAmount} />
          </ShinyText>
        ) : (
          <Payout amount={payoutAmount} />
        )}
      </span>
    </div>
  );
};

export const BetSummaryAction = () => {
  const { entry } = useBetSummaryContext();
  const [_, getModalProps] = useShareEntryModal();

  return (
    <ShareEntryAction
      {...getModalProps({
        referenceId: entry.referenceId,
        className: styles.action,
        icon: <ShareIcon />,
        entry: [entry, false],
      })}
    />
  );
};
